// What is rolled out, to how many, and whether you are one of them.
//
// A reading page like `RulesPage` and `TunerPage`, and behind the **same door**
// as the tuner: one audience, one password, one check (`TunerGate`). What it is
// for is the question a rollout makes somebody ask twice a day — "is this on
// for me, and is that because of the rollout or because I forced it" — which
// is otherwise answered by opening devtools and hashing a string by hand.
//
// **It reads; it does not write.** The rollout lives in the deployment's
// `FLAGS` variable, so changing one is a variable change and this page says how
// rather than offering a button that cannot work. The one thing it *can*
// change is your own tab, which is what the `?flag=` overrides already do and
// is safe for the reason the whole system is: a flag reaches presentation only.

import { useEffect, useState } from "react";

import { FLAGS, type FlagId, bucketOf } from "src/engine/flags/flags";
import { flagIsOn, flagVariant, visitorKey } from "src/engine/flags/visitor";
import { AdminBar, AdminNav, type AdminPage } from "./AdminNav";
import {
  Box,
  Btn,
  Notice,
  Page,
  PageColumn,
  Panel,
  Pill,
  styled,
  Table,
  TableScroll,
  Text,
  Link
} from "./kit";
import { TunerGated } from "./TunerGate";

export function FeaturesPage({
  onClose,
  onGo
}: {
  onClose: () => void;
  onGo: (page: AdminPage) => void;
}): React.ReactElement | null {
  return (
    <TunerGated
      title="Features"
      blurb="What is rolled out, to how much of the audience, and whether you are in it."
    >
      <Features onClose={onClose} onGo={onGo} />
    </TunerGated>
  );
}

/** Where a flag's share came from, which is the question after "is it on". */
type Source = "deployment" | "shipped";

/** The page over the app. Fixed, because it is a screen and not a panel. */
const Screen = styled(Page)(({ theme }) => ({
  position: "fixed",
  inset: 0,
  zIndex: theme.kit.z.chrome
}));

/** Monospaced, for the things that are typed rather than read. */
const Code = styled("code")(({ theme }) => ({
  fontFamily: theme.kit.font.display,
  fontSize: "0.95em"
}));

/**
 * Where this browser sits in the rollout, drawn. The bar is the share and the
 * mark is you — which is the whole explanation of why a flag is on or off, and
 * the one thing a percentage on its own cannot say.
 */
const Track = styled("div")(({ theme }) => ({
  position: "relative",
  width: 132,
  height: 8,
  borderRadius: theme.kit.radius.pill,
  background: `color-mix(in srgb, ${theme.kit.color.line} 60%, transparent)`,
  overflow: "hidden"
}));

const Share = styled("div", {
  shouldForwardProp: (prop: string) => prop !== "on"
})<{ on?: boolean }>(({ theme, on }) => ({
  height: "100%",
  borderRadius: theme.kit.radius.pill,
  background: on ? theme.kit.color.brand : theme.kit.color.inkDim,
  opacity: 0.7
}));

/** You, on that bar. Outside the clipped track, so it can overhang. */
const Mark = styled("div")(({ theme }) => ({
  position: "absolute",
  top: -3,
  width: 2,
  height: 14,
  background: theme.kit.color.ink,
  transform: "translateX(-1px)"
}));

const Wraps = styled("div")({ position: "relative", width: 132 });

const Spacer = styled("div")({ flex: 1 });

/** The `FLAGS` line, ready to paste. */
const Paste = styled("div")(({ theme }) => ({
  marginTop: theme.kit.space.md,
  padding: theme.kit.space.sm,
  borderRadius: theme.kit.radius.sm,
  background: `color-mix(in srgb, ${theme.kit.color.panel} 20%, ${theme.kit.color.ground})`,
  border: `2px solid ${theme.kit.color.line}`,
  color: theme.kit.color.cream,
  fontFamily: theme.kit.font.display,
  fontSize: 12,
  overflowX: "auto",
  whiteSpace: "nowrap"
}));

function Features({
  onClose,
  onGo
}: {
  onClose: () => void;
  onGo: (page: AdminPage) => void;
}): React.ReactElement {
  const [rollouts, setRollouts] = useState<Record<string, number> | null>(null);
  const [failed, setFailed] = useState(false);
  const key = visitorKey();

  useEffect(() => {
    let live = true;
    void fetch("/api/flags", { credentials: "same-origin" })
      .then((response) => (response.ok ? response.json() : null))
      .then((body: { rollouts?: Record<string, number> } | null) => {
        if (!live) return;
        if (body?.rollouts) setRollouts(body.rollouts);
        else setFailed(true);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);

  const forced = new URLSearchParams(window.location.search).getAll("flag");
  /** The `FLAGS` line that would reproduce what is running, ready to paste. */
  const spec = FLAGS.map((flag) => `${flag.id}=${rollouts?.[flag.id] ?? flag.rollout}`).join(",");

  return (
    <Screen>
      <AdminBar>
        <AdminNav current="features" onGo={onGo} />
        <Spacer />
        <Btn variant="ghost" small onClick={onClose}>
          Close
        </Btn>
      </AdminBar>
      <PageColumn width={980}>
        <Text block tone="dim" size={12}>
          A rollout is a share of visitors, held steady per browser. Nothing here reaches the
          rules — a flag decides what a screen looks like, never what a move is worth, so two
          players on opposite sides of a rollout are playing the same game.
        </Text>

        {failed && (
          <Notice tone="warn" style={{ marginTop: 16 }}>
            Could not reach <Code>/api/flags</Code> — showing what this build shipped with, which
            is what the app falls back to as well.
          </Notice>
        )}

        <Box sx={{ mt: 2 }}>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th>flag</th>
                  <th>you</th>
                  <th style={{ textAlign: "right" }}>rollout</th>
                  <th>where you sit</th>
                </tr>
              </thead>
              <tbody>
                {FLAGS.map((flag) => {
                  const rollout = rollouts?.[flag.id] ?? flag.rollout;
                  const source: Source =
                    rollouts && rollouts[flag.id] !== flag.rollout ? "deployment" : "shipped";
                  const bucket = bucketOf(flag.id, key);
                  const override = forced.includes(flag.id)
                    ? "forced on"
                    : forced.includes(`-${flag.id}`)
                      ? "forced off"
                      : null;
                  const on = flagIsOn(flag.id as FlagId, rollouts, key);
                  // **Which arm, for a flag that has them.** "On for you" is
                  // the honest answer to "am I in the experiment" and no answer
                  // at all to the question a multi-way rollout makes somebody
                  // ask, which is what they are actually seeing.
                  const arm = flagVariant<string | number, null>(
                    flag.id as FlagId,
                    null,
                    rollouts,
                    key
                  );
                  return (
                    <tr key={flag.id}>
                      <td>
                        <Text role="display" block weight={700} size={14}>
                          {flag.id}
                        </Text>
                        <Text block tone="dim" size={12}>
                          {flag.detail}
                        </Text>
                        {"variants" in flag && (
                          // The arms and their sizes, because the percentage
                          // beside is the share in the experiment *at all* and
                          // says nothing about how it is divided.
                          <Text block tone="dim" size={11}>
                            arms: {(flag.variants as readonly (string | number)[]).join(" · ")} —
                            split evenly,{" "}
                            {Math.round(
                              (rollout / (flag.variants as readonly unknown[]).length) * 100
                            )}
                            % each, the rest on today&apos;s game
                          </Text>
                        )}
                      </td>
                      <td>
                        <Pill>
                          <Text tone={on ? "brand" : "dim"} weight={700} size={12}>
                            {on ? "on" : "off"}
                          </Text>
                        </Pill>
                        {arm !== null && (
                          <Text block role="display" tone="cream" size={11}>
                            arm {arm}
                          </Text>
                        )}
                        {override && (
                          // **Said out loud**, because a forced flag is the one
                          // state where the page would otherwise disagree with
                          // the rollout beside it and look wrong.
                          <Text block tone="rust" weight={700} size={11}>
                            {override}
                          </Text>
                        )}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Text role="display" block tone="brand" weight={700} size={18}>
                          {Math.round(rollout * 100)}%
                        </Text>
                        <Text block tone="dim" size={11}>
                          {source === "deployment" ? "set by deployment" : "shipped default"}
                        </Text>
                      </td>
                      <td>
                        <Wraps>
                          <Track>
                            <Share on={on} style={{ width: `${rollout * 100}%` }} />
                          </Track>
                          <Mark style={{ left: `${bucket * 100}%` }} />
                        </Wraps>
                        <Text block role="display" tone="dim" size={11}>
                          {(bucket * 100).toFixed(1)}%
                        </Text>
                        <Link href={`?flag=${on ? "-" : ""}${flag.id}`}>
                          <Text tone="brand" size={11}>
                            see it {on ? "off" : "on"}
                          </Text>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
        </Box>

        {/* **How to change it**, rather than a control that cannot. The share
            lives in the deployment's variable, so the honest thing a read-only
            page can offer is the exact line to paste. */}
        <Panel solid style={{ marginTop: 24, padding: 16 }}>
          <Text block weight={700} size={14}>
            Changing a rollout
          </Text>
          <Text block size={13}>
            Set <Code>FLAGS</Code> on the service. A share or a percentage — <Code>0.25</Code> and{" "}
            <Code>25</Code> both mean a quarter. Anything not named keeps the default this build
            shipped with, so the variable lists only what you are changing. It takes effect on the
            next restart; no rebuild.
          </Text>
          <Paste>FLAGS={spec}</Paste>
          <Text block tone="dim" size={11}>
            That line reproduces what is running now. Your browser is <Code>{key}</Code> — clearing
            site data draws a new one and may move you across a rollout.
          </Text>
        </Panel>
      </PageColumn>
    </Screen>
  );
}
